import { Table, Tooltip } from 'antd';
import { ColumnType } from 'antd/lib/table';
import moment from 'moment';
import React, { FC } from 'react';
import styled from 'styled-components';
import { ProjectId, TaskId } from 'api/models';
import { DueDate } from 'components/atoms/due-date';
import { TaskNameCell } from 'components/molecules/tasks/task-name-cell';
import styles from 'components/organisms/home/overview/overview.module.css';
import { PriorityCell } from 'components/organisms/home/overview/render-priority';
import { Text02 } from 'components/styles';
import { useProject } from 'data/projects/hooks';
import { Task } from 'data/tasks';

interface ListProps {
  data: Task[];
  onItemClick: (item: Task) => void;
}

const getProjectName = (projectName = '', projectPhaseName = '') => {
  return [projectName, projectPhaseName].filter(Boolean).join(' > ');
};

const ProjectCell = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${Text02};
`;
const ProjectBreadCrumbCell = ({ task }: { task: Task }) => {
  const project = useProject(ProjectId(task.project));

  const name = project
    ? getProjectName(project.projectName, task.projectPhase?.projectPhaseName)
    : null;

  return project ? (
    <Tooltip placement="topLeft" title={name}>
      <ProjectCell>{name}</ProjectCell>
    </Tooltip>
  ) : null;
};

const StyledTaskNameCell = styled(TaskNameCell)`
  margin-left: var(--spacing-03);
`;

function rowClassName(record: Task): string {
  return record.private ? styles.private : '';
}

export const Section: FC<ListProps> = (props) => {
  const { data, onItemClick } = props;

  const COLUMNS: ColumnType<Task>[] = [
    {
      dataIndex: 'taskName',
      key: 'taskName',
      width: '50%',
      align: 'left',
      render: (value, record: Task) => (
        <StyledTaskNameCell
          taskName={record.taskName}
          taskId={TaskId(record.project, record.taskId)}
          fields={record.fields}
          atRisk={record.atRisk}
          progress={record.progress}
          taskType={record.type}
          size="small"
          readOnly={false}
        />
      ),
    },
    {
      width: '30%',
      align: 'left',
      render: (value, record: Task) => <ProjectBreadCrumbCell task={record} />,
    },
    {
      className: styles.cell,
      dataIndex: 'priority',
      align: 'center',
      key: 'priority',
      width: '120px',
      render: (value: string, record: Task) => (
        <PriorityCell fields={record.fields} />
      ),
    },
    {
      className: styles.cell,
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: '120px',
      align: 'right',
      render: (value, record) =>
        value && (
          <DueDate
            timestamp={moment(value)}
            completed={Boolean(record.completedAt)}
          />
        ),
    },
  ];

  return (
    <Table
      rowKey="taskId"
      className={styles.list_table}
      showHeader={false}
      columns={COLUMNS}
      dataSource={data}
      pagination={false}
      tableLayout="fixed"
      rowClassName={rowClassName}
      onRow={(record) => ({
        onClick: () => {
          onItemClick(record as unknown as Task);
        },
      })}
    />
  );
};
